import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import {
  CheckInDigest,
  ProgressionReadiness,
  Trend,
  assessProgression,
  buildCheckInDigest,
  summariseDigest,
} from '../../utils/apneaProgress';
import { ApneaSessionDto } from '../../types/Apnea';
import { formatSeconds } from '../../utils/apneaTables';
import { LEARN_ANCHORS } from './LearnReference';

/**
 * Check-in panel.
 *
 * Shows what the log actually says, then a progression recommendation with its
 * reasoning. The recommendation is advisory — the diver decides — but the reasons
 * are always shown so a "reduce" never looks arbitrary.
 *
 * The subjective half of a check-in (how did it feel, were you sleeping well) is
 * what the Nereus conversation is for; this panel covers the measurable half.
 */

const TrendIcon: React.FC<{ trend: Trend }> = ({ trend }) => {
  if (trend.direction === 'improving') return <TrendingUp fontSize="small" color="success" />;
  if (trend.direction === 'declining') return <TrendingDown fontSize="small" color="warning" />;
  if (trend.direction === 'steady') return <TrendingFlat fontSize="small" color="action" />;
  return null;
};

const TrendRow: React.FC<{ label: string; trend: Trend; hint?: React.ReactNode }> = ({
  label, trend, hint,
}) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
    <Typography variant="body2" sx={{ minWidth: 120 }}>{label}</Typography>
    <TrendIcon trend={trend} />
    <Typography variant="body2" color="text.secondary">
      {trend.direction === 'insufficient'
        ? `not enough data yet (${trend.sampleCount} rated)`
        : trend.direction}
      {trend.earlier !== null && trend.later !== null && (
        <> · {trend.earlier.toFixed(1)} → {trend.later.toFixed(1)}</>
      )}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" component="span">{hint}</Typography>
    )}
  </Stack>
);

const RECOMMENDATION_COPY: Record<ProgressionReadiness['recommendation'], {
  severity: 'success' | 'info' | 'warning';
  title: string;
}> = {
  progress: { severity: 'success', title: 'The evidence supports progressing' },
  repeat: { severity: 'info', title: 'Repeat the current level' },
  reduce: { severity: 'warning', title: 'Reduce the workload' },
};

interface CheckInPanelProps {
  sessions: ApneaSessionDto[];
  windowDays?: number;
  /** Opens the Nereus conversation with the digest as context. */
  onDiscussWithNereus?: (digest: CheckInDigest) => void;
}

const CheckInPanel: React.FC<CheckInPanelProps> = ({
  sessions,
  windowDays = 14,
  onDiscussWithNereus,
}) => {
  const [showDetail, setShowDetail] = useState(false);

  const windowed = useMemo(() => {
    const cutoff = Date.now() - windowDays * 86_400_000;
    return sessions.filter(s => new Date(s.startedAt).getTime() >= cutoff);
  }, [sessions, windowDays]);

  const digest = useMemo(() => buildCheckInDigest(windowed, windowDays), [windowed, windowDays]);
  const readiness = useMemo(() => assessProgression(digest), [digest]);
  const summary = useMemo(() => summariseDigest(digest), [digest]);

  const copy = RECOMMENDATION_COPY[readiness.recommendation];

  if (windowed.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Check-in</Typography>
        <Typography variant="body2" color="text.secondary">
          No sessions in the last {windowDays} days. Log a few and the check-in will have
          something to work from.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="subtitle1">Check-in · last {windowDays} days</Typography>
        <Chip
          size="small"
          label={`${digest.adherence.daysTrained} days trained`}
          variant="outlined"
        />
      </Stack>

      <Alert severity={copy.severity} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{copy.title}</Typography>
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {readiness.reasons.map(reason => (
            <ListItem key={reason} disableGutters sx={{ py: 0 }}>
              <ListItemText
                primary={reason}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Alert>

      <Stack spacing={0.5}>
        {summary.map(line => (
          <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
        ))}
      </Stack>

      <Button size="small" onClick={() => setShowDetail(v => !v)} sx={{ mt: 1 }}>
        {showDetail ? 'Hide trends' : 'Show trends'}
      </Button>

      {showDetail && (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          <TrendRow
            label="Recovery"
            trend={digest.recovery}
            hint={
              <Link component={RouterLink} to={`?tab=learn#${LEARN_ANCHORS.overtrainingSignals}`}>
                the progression gate — why?
              </Link>
            }
          />
          <TrendRow label="Relaxation" trend={digest.relaxation} />
          <TrendRow label="Technique" trend={digest.technique} />
          <TrendRow label="Perceived effort" trend={digest.effort} />
          <TrendRow
            label="1st contraction"
            trend={digest.contractions.trend}
            hint={
              <>
                {digest.contractions.averageSeconds !== null && (
                  <>avg {formatSeconds(Math.round(digest.contractions.averageSeconds))} · </>
                )}
                <Link component={RouterLink} to={`?tab=learn#${LEARN_ANCHORS.contractions}`}>
                  what's this?
                </Link>
              </>
            }
          />

          {digest.maxStatic.latestSeconds !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Last max static {formatSeconds(digest.maxStatic.latestSeconds)}
              {digest.maxStatic.latestAt && (
                <> on {new Date(digest.maxStatic.latestAt).toLocaleDateString()}</>
              )}
            </Typography>
          )}

          {digest.walks.averageDistanceMeters !== null && (
            <Typography variant="body2" color="text.secondary">
              Apnea walks averaging {Math.round(digest.walks.averageDistanceMeters)} m
              over {digest.walks.sessions} session(s)
            </Typography>
          )}

          {digest.bolt.manualCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {digest.bolt.manualCount} BOLT reading(s) were entered by hand — slightly weaker
              evidence than a timed one.
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        This covers what the log can measure. How the sessions actually felt, and whether you
        were sleeping and eating well, matters just as much — that is worth talking through.
      </Typography>

      {onDiscussWithNereus && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => onDiscussWithNereus(digest)}>
            Talk it through with Nereus
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CheckInPanel;

/**
 * Compact prose digest for handing to the chat. Deliberately a summary rather than
 * raw session history, so the conversation starts from trends and the model is not
 * left to do arithmetic on a log dump.
 */
export const digestToPrompt = (digest: CheckInDigest): string => {
  const readiness = assessProgression(digest);
  const lines = [
    `Here is my apnea training over the last ${digest.windowDays} days:`,
    ...summariseDigest(digest).map(l => `- ${l}`),
  ];

  if (digest.recovery.direction !== 'insufficient') {
    lines.push(`- Recovery trend: ${digest.recovery.direction}.`);
  }
  if (digest.relaxation.direction !== 'insufficient') {
    lines.push(`- Relaxation trend: ${digest.relaxation.direction}.`);
  }
  if (digest.contractions.trend.direction !== 'insufficient') {
    lines.push(`- First contraction trend: ${digest.contractions.trend.direction}.`);
  }
  if (digest.maxStatic.latestSeconds !== null) {
    lines.push(`- Last max static: ${formatSeconds(digest.maxStatic.latestSeconds)}.`);
  }

  lines.push(
    `The app's read is "${readiness.recommendation}" because: ${readiness.reasons.join(' ')}`,
    'Does that match what you would say? What should I focus on next, and what would you '
    + 'want to know that the log does not show?'
  );

  return lines.join('\n');
};
